// src/components/MobileReservationButton.js
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import ServicesDrawer from "./ServicesDrawer";

// ReservaWidget: carga el script de Book Rides Online y muestra el formulario real, con botón CLOSE al final
function ReservationWidget({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const scriptId = "reservationScript";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://bookridesonline.com/web/reservation.js";
      script.async = true;

      script.onload = () => setLoading(false);
      script.onerror = () => {
        setError("Error loading the reservation system. Please try again later.");
        setLoading(false);
      };

      document.body.appendChild(script);
    } else {
      setLoading(false);
    }
  }, []);

  // Hack opcional para ocultar alerta de SSL/credit card (si existiera)
  useEffect(() => {
    if (!loading && !error) {
      const timer = setTimeout(() => {
        const alerts = document.querySelectorAll('div.alert, div.bro-error');
        alerts.forEach(alert => {
          const text = alert.innerText.toLowerCase();
          if (text.includes('credit card payment') || text.includes('not ssl encrypted')) {
            alert.style.display = 'none';
          }
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, error]);

  return (
    <div style={{ textAlign: 'center', padding: '10px' }}>
      {loading && (
        <div style={{ fontWeight: 'bold', margin: '20px' }}>
          Loading reservation form...
        </div>
      )}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {/* Contenedor donde Book Rides Online inyecta el formulario */}
      <div id="bro_reservation"></div>

      {/* Campos ocultos requeridos */}
      <input type="hidden" id="cid" value="5CRSVz2LXA2x7Wz0JlS0uQ%3d%3d" />
      <input type="hidden" id="dbaid" value="27Tezv2FIvv7%2fytBuYKNdA%3d%3d" />

      <div style={{ fontSize: '1.2rem', marginTop: '10px', fontWeight: 'bold' }}>
        Complete Your Reservation
      </div>

      {/* Botón CLOSE al final */}
      <button onClick={onClose} style={styles.closeButton}>
        CLOSE
      </button>
    </div>
  );
}

// BookNowModal: se muestra si isOpen es true y envía onClose a ReservationWidget
function BookNowModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.scrollArea}>
          <ReservationWidget onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

// MobileReservationButton: muestra cuatro botones flotantes y SEO avanzado
function MobileReservationButton() {
  const [isOpen, setIsOpen] = useState(false); // Modal para BOOK NOW
  const [showServicesDrawer, setShowServicesDrawer] = useState(false);

  const blinkAnimation = `
    @keyframes blink {
      0% { opacity: 1; }
      50% { opacity: 0.4; }
      100% { opacity: 1; }
    }
  `;

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "HERITAGE CAR AIRPORT",
    "foundingDate": "2005",
    "identifier": "DOS ID #7502116",
    "image": "https://heritagecarairport.com/images/logo.jpg",
    "@id": "https://heritagecarairport.com/",
    "url": "https://heritagecarairport.com/",
    "telephone": "+1-914-276-2961",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "395 Hallocks Mill Rd",
      "addressLocality": "Yorktown Heights",
      "addressRegion": "NY",
      "postalCode": "10598",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.2789",
      "longitude": "-73.7837"
    },
    "priceRange": "$$",
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }],
    "description": "Heritage Car Airport provides premium taxi services for efficient airport transfers and local trips with luxury and reliability.",
    "sameAs": [
      "https://www.facebook.com/yourpage",
      "https://www.instagram.com/yourprofile"
    ]
  };

  const handleCall = () => {
    window.open('tel:19142762961', '_self');
  };

  const handleText = () => {
    window.open('sms:19142762961', '_self');
  };

  return (
    <>
      <Helmet>
        <title>Book Now - HERITAGE CAR AIRPORT</title>
        <meta name="description" content="Floating buttons to book your ride, call us, or send an SMS. Safe and reliable taxi service." />
        <meta name="robots" content="index,follow" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="HERITAGE CAR AIRPORT - Book, Call or Text" />
        <meta property="og:description" content="Reliable taxi services for airport transfers and local trips. Book your ride online or call/sms now." />
        <meta property="og:url" content="https://heritagecarairport.com" />
        <meta property="og:image" content="https://heritagecarairport.com/images/your-og-image.jpg" />
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="HERITAGE CAR AIRPORT - 24/7 Rides" />
        <meta name="twitter:description" content="Fast and safe taxi service in Yorktown Heights, NY. Book now or call us 24/7." />
        <meta name="twitter:image" content="https://heritagecarairport.com/images/your-og-image.jpg" />
        
        {/* JSON-LD para LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessJsonLd)}
        </script>
      </Helmet>

      <style>{blinkAnimation}</style>

      <div style={styles.container}>
        <button
          style={{ ...styles.fullWidthButton, animation: 'blink 1.5s infinite' }}
          onClick={() => setIsOpen(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.03)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = styles.fullWidthButton.boxShadow;
          }}
        >
          BOOK NOW
        </button>

        <button
          style={{ ...styles.fullWidthButton, animation: 'blink 1.5s infinite' }}
          onClick={handleCall}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.03)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = styles.fullWidthButton.boxShadow;
          }}
        >
          CALL US
        </button>

        <button
          style={{ ...styles.fullWidthButton, animation: 'blink 1.5s infinite' }}
          onClick={handleText}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.03)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = styles.fullWidthButton.boxShadow;
          }}
        >
          TEXT US
        </button>

        <button
          style={{ ...styles.fullWidthButton, animation: 'blink 1.5s infinite' }}
          onClick={() => setShowServicesDrawer(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.03)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = styles.fullWidthButton.boxShadow;
          }}
        >
          SERVICES
        </button>
      </div>

      <BookNowModal isOpen={isOpen} onClose={() => setIsOpen(false)} />

      <ServicesDrawer
        isOpen={showServicesDrawer}
        onClose={() => setShowServicesDrawer(false)}
      />
    </>
  );
}

const styles = {
  container: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
  },
  fullWidthButton: {
    width: '100%',
    background: 'linear-gradient(90deg, #586D79 0%, #44535E 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    margin: '4px 0',
    padding: '15px 0',
    fontSize: '1rem',
    fontWeight: 'bold',
    boxShadow: '0 5px 0 #000000, 0 10px 15px rgba(0,0,0,0.3)',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    textAlign: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
  },
  scrollArea: {
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  closeButton: {
    display: 'block',
    margin: '20px auto 0 auto',
    background: '#586D79',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '1rem',
    padding: '8px 16px',
    cursor: 'pointer',
  },
};

export default MobileReservationButton;
